import * as React from 'react';
import { Canceler } from 'axios';

import API from 'src/services/api';
import { TooltipIcon, Button } from 'src/components';

interface IUploadFormState {
  isUploading: boolean;
  uploadStatus: { status: string; message: string } | null;
  mailInputValue: string;
  mails: string[];
  file: File | null;
}

export default class UploadForm extends React.Component<{}, IUploadFormState> {
  reqCanceler: Canceler | null = null;
  fileInputRef = React.createRef<HTMLInputElement>();

  state: IUploadFormState = {
    isUploading: false,
    uploadStatus: null,
    mailInputValue: '',
    mails: [],
    file: null,
  };

  componentWillUnmount() {
    if (this.reqCanceler) {
      this.reqCanceler();
    }
  }

  uploadFile = async () => {
    const { mails, file } = this.state;
    if (file) {
      this.setState({ isUploading: true, uploadStatus: null });
      try {
        const { getData, cancel } = API.LINK_UPLOAD.uploadTemplate(
          mails.join(';'),
          file
        );
        this.reqCanceler = cancel;
        await getData();
        this.reqCanceler = null;

        this.resetFile();
        this.setState({
          uploadStatus: {
            status: 'success',
            message: 'Uploaded successfully',
          },
        });
      } catch (error) {
        this.setState({
          uploadStatus: {
            status: 'error',
            message: 'Uploading failed',
          },
        });
      }
      this.setState({ isUploading: false });
    }
  };

  addMail = () => {
    if (this.state.mailInputValue === '') {
      return;
    }
    this.setState((state) => ({
      mails: [...state.mails, state.mailInputValue],
      mailInputValue: '',
    }));
  };

  removeMail = (mail: string) => {
    this.setState((state) => ({
      mails: state.mails.filter((el) => el !== mail),
    }));
  };

  onMailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const mail = event.target.value;
    this.setState({ mailInputValue: mail.trim() });
  };

  onMailKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (this.state.mailInputValue.length === 0) {
      return;
    }
    if (event.key === ' ' || event.key === 'Enter') {
      this.addMail();
    }
  };

  onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    this.setState({
      file: files ? files[0] : null,
      uploadStatus: null,
    });
  };

  resetFile = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.value = '';
    }
    this.setState({
      file: null,
    });
  };

  render() {
    const { file, mails, isUploading, uploadStatus, mailInputValue } =
      this.state;

    return (
      <div>
        <div className="flex mb-6 relative">
          <label className="w-48 pt-2 pr-6" htmlFor="file">
            Excel template file*
          </label>
          <div className="flex-1">
            <div className="flex items-center">
              <input
                ref={this.fileInputRef}
                id="file"
                className="absolute pin-l pin-t opacity-0 w-1 h-1"
                type="file"
                accept=".xls,.xlsx"
                onChange={this.onFileChange}
              />
              <label
                className="flex-1 px-3 py-2 leading-normal rounded-lg focus:outline-none focus:shadow-outline bg-white shadow"
                htmlFor="file"
              >
                {file ? file.name : 'Select a file'}
              </label>
              {file && (
                <button
                  onClick={this.resetFile}
                  className="absolute pin-r pin-t pin-b py-2 px-3 hover:text-yellow"
                >
                  &times;
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex mb-6">
          <label className="w-48 pt-2 pr-6" htmlFor="cc">
            Confirmation mail CC
          </label>
          <div className="flex-1">
            <div className="flex items-center">
              <div className="flex-1 rounded-lg bg-white shadow py-1 px-3 focus:outline-none focus:shadow-outline">
                {mails.map((mail, index) => (
                  <span
                    className="inline-block rounded bg-grey p-2 py-1 my-1 mr-3"
                    key={index}
                  >
                    {mail}
                    <button
                      type="button"
                      className="ml-2 hover:text-yellow"
                      onClick={this.removeMail.bind(null, mail)}
                    >
                      ×
                    </button>
                  </span>
                ))}
                <input
                  id="cc"
                  type="text"
                  className="w-128 py-1 my-1 focus:outline-none"
                  onChange={this.onMailChange}
                  onKeyDown={this.onMailKeyDown}
                  onBlur={this.addMail}
                  value={mailInputValue}
                  placeholder="Add an email"
                  required={true}
                />
              </div>

              <TooltipIcon
                className="ml-4"
                width="w-64"
                text={
                  <>
                    Additional recipients for the confirmation mail (not
                    mandatory). Type an email and press Space or Enter.
                  </>
                }
              />
            </div>
          </div>
        </div>
        <div className="ml-48">
          <Button
            disabled={!file || isUploading}
            loading={isUploading}
            loadingLabel="Uploading file"
            onClick={this.uploadFile}
          >
            Upload file
          </Button>
          {uploadStatus && (
            <p
              className={`inline-block ml-6 ${
                uploadStatus.status === 'success' ? 'text-green' : 'text-red'
              } `}
            >
              {uploadStatus.message}
            </p>
          )}
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/views/LinkCreateBulkExcel/components/UploadForm.tsx